import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { paths } from '@/app/paths';
import { showToast } from '@/features/toast/toastSlice';
import { extractRejectionReason } from '@/utils/safeErrorMessage';

/**
 * Renders a deal, client or company page only once its record has loaded. A record that is
 * missing or forbidden shows a toast and goes back to the list; a numeric HubSpot id from an
 * old link is swapped for the doc id.
 */
const RecordRouteGuard = ({ id, record, isLoading, error, label, listPath, children }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname, search, state } = useLocation();

	const isMissing = !isLoading && !record;
	const isLegacyId = Boolean(record?.id) && /^\d+$/.test(id);

	useEffect(() => {
		if (!isMissing) return;
		dispatch(
			showToast({
				message:
					extractRejectionReason(error) === 'USER_FORBIDDEN'
						? `Error: you do not have the correct permissions to access ${label} ${id}.`
						: `Error: ${label} ${id} does not exist. It may have been deleted or you may not have the correct permissions.`,
				id: `${id}-invalid`,
				error: true,
				duration: 5000,
				autohide: true,
			}),
		);
		// Default to the list this record sits under, e.g. /new-business for /new-business/:dealId/tasks
		navigate(listPath ?? (pathname.slice(0, pathname.indexOf(`/${id}`)) || paths.home), {
			replace: true,
		});
	}, [isMissing, id]);

	useEffect(() => {
		if (!isLegacyId) return;
		const path = pathname
			.split('/')
			.map((segment) => (segment === id ? record.id : segment))
			.join('/');
		navigate(`${path}${search}`, { replace: true, state });
	}, [isLegacyId, id]);

	// Keep showing the record while it refetches, e.g. after an edit invalidates it
	if (!record || isLegacyId) {
		return (
			<Box pt={1} display='flex' justifyContent='center'>
				<CircularProgress />
			</Box>
		);
	}

	return children;
};

export default RecordRouteGuard;
